// Page: StockVideos
// Purpose: Public app page to browse and preview stock videos. Supports search,
// tag filters, saved-only view (per namespace), premium gating, infinite scroll,
// and modal preview with save/unsave actions.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '../../components/ui/overlays/Tooltip';
import StockVideoFilters from '../../components/templates/templates/StockVideoFilters';
import StockVideoGrid from '../../components/templates/templates/StockVideoGrid';
import StockVideoModal from '../../components/templates/templates/StockVideoModal';
import { getStockVideos } from '../../api/media/stockVideosApi';
import { saveStockVideo, unsaveStockVideo, getSavedStockVideoIds } from '../../api/media/stockVideoActionsApi';
import { useAuth } from '../../context/auth';
import { useNamespace } from '../../context/namespace';
import { useToast } from '../../components/ui/notifications/toast-notification.jsx';
import Footer from '../../components/shared/Footer';
import { AlertCircle, Search, X, Loader2, Crown, ChevronRight, HelpCircle } from 'lucide-react';
import { BASIC_TEMPLATE_PAGE_LIMIT, STOCK_VIDEOS_PER_PAGE } from '../../data/limits/templateLimits';
import SEOHead from '../../components/shared/SEOHead';

// Add keyframe animation for fade-in
const fadeInAnimation = `
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;


// Stock Video Search Component (matching TemplateSearch exactly)
const StockVideoSearch = ({ 
  searchQuery, 
  setSearchQuery, 
  onSearch, 
  onClearSearch, 
  isLoading, 
  isBasicUser = false
}) => {
  const [inputValue, setInputValue] = useState(searchQuery);

  // Sync inputValue with searchQuery when searchQuery changes from outside
  useEffect(() => {
    setInputValue(searchQuery);
  }, [searchQuery]);

  const handleSearchChange = (e) => {
    if (isBasicUser) return; // Basic users can't search
    setInputValue(e.target.value);
  };

  const clearSearch = () => {
    if (isBasicUser) return; // Basic users can't clear search
    setInputValue('');
    if (onClearSearch) {
      onClearSearch();
    } else {
      setSearchQuery('');
      onSearch('');
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (isBasicUser) return; // Basic users can't submit search
    setSearchQuery(inputValue);
    onSearch(inputValue);
  };

  return (
    <div className="relative mb-6">
      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className={`h-5 w-5 ${isBasicUser ? 'text-gray-500' : 'text-gray-400'}`} />
          </div>
          <input
            type="text"
            className={`block w-full pl-10 py-2.5 border border-gray-300 rounded-lg ${
              isBasicUser
                ? 'bg-gray-100 cursor-not-allowed opacity-60' 
                : 'bg-white'
            } dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
            placeholder={
              isBasicUser 
                ? "Search unavailable for basic plan" 
                  : "Search stock videos..."
            }
            value={inputValue}
            onChange={handleSearchChange}
            disabled={isBasicUser}
          />
        </div>
        <button
          type="submit"
          disabled={!inputValue.trim() || isLoading || isBasicUser || inputValue.trim() === searchQuery.trim()}
          className={`px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors flex items-center gap-2 ${
            !inputValue.trim() || isBasicUser || inputValue.trim() === searchQuery.trim()
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500' 
              : isLoading
                ? 'bg-primary-500 text-white cursor-wait'
                : 'bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600 text-white'
          }`}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Searching...</span>
            </>
          ) : (
            <span>Search</span>
          )}
        </button>
        
        {searchQuery && !isBasicUser && (
          <button
            type="button"
            onClick={clearSearch}
            className="px-3 py-2.5 rounded-lg flex items-center transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            title="Clear search"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </form>
    </div>
  );
};

function StockVideos() {
  const navigate = useNavigate();
  
  // Add the style tag for animations
  useEffect(() => {
    // Add the animation styles to the document head
    const styleTag = document.createElement('style');
    styleTag.innerHTML = fadeInAnimation;
    document.head.appendChild(styleTag);
    
    // Clean up on unmount
    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState(null);
  const [selectedStockVideo, setSelectedStockVideo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortMethod, setSortMethod] = useState('random');
  const [showSavedStockVideos, setShowSavedStockVideos] = useState(false);
  const [savedStockVideoIds, setSavedStockVideoIds] = useState(new Set());
  const [savedStockVideosLoaded, setSavedStockVideosLoaded] = useState(false);
  const [loadingSavedStockVideos, setLoadingSavedStockVideos] = useState(false);

  // State for API data and pagination
  const [stockVideos, setStockVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [error, setError] = useState(null);
  const [allStockVideosLoaded, setAllStockVideosLoaded] = useState(false);
  const [isDataReady, setIsDataReady] = useState(false); // Track if we have confirmed data state (either videos or confirmed empty)
  
  // Get auth context and toast
  const { user } = useAuth();
  const { addToast } = useToast?.() || { addToast: (msg) => console.log(msg) };
  
  // Get namespace context
  const { namespaces, currentNamespace } = useNamespace();
  
  // Find the current namespace ID from the namespaces array
  const currentNamespaceObj = namespaces.find(ns => ns.name === currentNamespace);
  const currentNamespaceId = currentNamespaceObj?.id;
  
  // Simplified access control - Enterprise users get same access as Premium users
  const isPremiumUser = user?.planLevel === 'premium';
  const isEnterpriseUser = user?.isEnterpriseUser === true || user?.isEnterprise === true;
  
  // All enterprise users get full stock video access (same as premium)
  const hasStockVideoAccess = isPremiumUser || isEnterpriseUser;
  const hasFullStockVideoAccess = isPremiumUser || isEnterpriseUser;
  const isBasicUser = !hasFullStockVideoAccess;
  
  // We'll use this to track if basic user has reached their page limit
  const hasReachedBasicPageLimit = isBasicUser && page >= BASIC_TEMPLATE_PAGE_LIMIT;
  
  // Refs for scroll handling and API request tracking
  
  const lastLoadTimeRef = useRef(0);
  const apiRequestInProgressRef = useRef(false);
  const lastRequestParamsRef = useRef(null);
  const requestCountRef = useRef(0);
  const sentinelRef = useRef(null);
  
  // Add intersection observer refs
  const loadingMoreRef = useRef(false);



  // Get search type for sorting
  const getSearchType = (method) => {
    switch (method) {
      case 'newest': return 'newest';
      case 'random': return 'random';
      default: return 'random';
    }
  };

  // Function to load stock videos
  const loadStockVideos = useCallback(async (pageNum = 1, resetList = false) => {
    // Reset data ready state when starting a new load (except for pagination)
    if (pageNum === 1) {
      setIsDataReady(false);
    }
    
    // Allow loading for all users, but apply restrictions for non-premium users
    // Non-premium users will see videos but with restrictions applied in the UI

    if (apiRequestInProgressRef.current && pageNum === 1) {
      console.log('API request already in progress for first page, skipping...');
      return;
    }
    
    if (apiRequestInProgressRef.current && pageNum > 1) {
      console.log('API request already in progress for pagination, skipping...');
      return;
    }

    const now = Date.now();
    if (now - lastLoadTimeRef.current < 500 && pageNum > 1) {
      console.log('Rate limiting API requests');
      return;
    }

    // Check if we've reached the basic user page limit for non-premium users
    if (!hasStockVideoAccess && pageNum > 1) {
      console.log('Non-premium user is limited to first page only');
      setHasMore(false);
      setIsDataReady(true);
      return;
    }

    const currentParams = {
      tags: Array.isArray(activeFilter) ? activeFilter.join(',') : activeFilter,
      search: searchQuery.trim(),
      sortBy: getSearchType(sortMethod),
      page: pageNum,
      limit: STOCK_VIDEOS_PER_PAGE,
      savedOnly: showSavedStockVideos
    };

    // Avoid duplicate requests with same parameters
    const paramsKey = JSON.stringify(currentParams);
    if (lastRequestParamsRef.current === paramsKey && pageNum === 1) {
      console.log('Duplicate request detected for same parameters, skipping...');
      return;
    }

    try {
      apiRequestInProgressRef.current = true;
      lastLoadTimeRef.current = now;
      lastRequestParamsRef.current = paramsKey;
      
      if (pageNum === 1) {
        if (resetList) {
          setLoading(true);
        } else {
          setSearchLoading(true);
        }
        setError(null);
      } else {
        setLoading(true);
      }

      requestCountRef.current += 1;
      const requestId = requestCountRef.current;

      console.log(`Loading stock videos (request ${requestId}):`, currentParams);

      // Load saved stock video IDs if needed (only for premium users)
      if (showSavedStockVideos && !savedStockVideosLoaded && currentNamespaceId && hasStockVideoAccess) {
        await loadSavedStockVideoIds();
      }

      const response = await getStockVideos({
        tags: currentParams.tags, // Already joined as comma-separated string
        search: currentParams.search,
        sortBy: currentParams.sortBy,
        page: currentParams.page,
        limit: currentParams.limit
      });

      // Check if this is still the latest request
      if (requestId !== requestCountRef.current) {
        return; // Silently ignore outdated responses
      }

      if (response && Array.isArray(response.stockVideos)) {
        let newStockVideos = response.stockVideos || [];

        // Filter by saved videos if needed (only for premium users)
        if (showSavedStockVideos && hasStockVideoAccess) {
          newStockVideos = newStockVideos.filter(video => savedStockVideoIds.has(video.id));
        }

        // For non-premium users, limit to first 5 videos as a preview
        if (!hasStockVideoAccess && pageNum === 1) {
          newStockVideos = newStockVideos.slice(0, 5);
        }

        if (pageNum === 1 || resetList) {
          setStockVideos(newStockVideos);
        } else {
          setStockVideos(prev => {
            const existingIds = new Set(prev.map(v => v.id));
            const uniqueNewVideos = newStockVideos.filter(v => !existingIds.has(v.id));
            return [...prev, ...uniqueNewVideos];
          });
        }

        setPage(pageNum);
        // Non-premium users have no more pages
        setHasMore(hasStockVideoAccess && (response.hasMore ?? (newStockVideos.length === STOCK_VIDEOS_PER_PAGE)));
        
        if (!response.hasMore || newStockVideos.length < STOCK_VIDEOS_PER_PAGE || !hasStockVideoAccess) {
          setAllStockVideosLoaded(true);
        }
        
        // Mark data as ready only for first page loads (not pagination)
        if (pageNum === 1) {
          setIsDataReady(true);
        }
      } else {
        console.error('Invalid API response format:', response);
        setError('Invalid response format from server');
        // Mark data as ready even for errors on first page
        if (pageNum === 1) {
          setIsDataReady(true);
        }
      }
    } catch (err) {
      console.error('Error loading stock videos:', err);
      
      // Check for premium access error
      if (err.response?.status === 403) {
        setError('Premium access required. Please upgrade your plan to access stock videos.');
      } else if (err.message?.includes('404') || err.status === 404) {
        setError('Stock videos feature is not yet available on this server. Please check back later.');
      } else {
        setError(err.message || 'Failed to load stock videos. Please try again.');
      }
      
      if (pageNum === 1) {
        setStockVideos([]);
        // Mark data as ready even for errors on first page
        setIsDataReady(true);
      }
    } finally {
      apiRequestInProgressRef.current = false;
      setLoading(false);
      setSearchLoading(false);
      setInitialLoading(false);
    }
  }, [activeFilter, searchQuery, sortMethod, showSavedStockVideos, savedStockVideoIds, savedStockVideosLoaded, hasStockVideoAccess]);

  // Function to load more stock videos (for infinite scroll)
  const loadMoreStockVideos = useCallback(async () => {
    // Don't load more if:
    // 1. Already loading
    // 2. No more videos to load  
    // 3. Already got all videos
    // 4. API request is already in progress
    // 5. Currently loading more (from our local ref)
    // 6. User doesn't have access to load more
    if (
      loading || 
      !hasMore || 
      allStockVideosLoaded || 
      apiRequestInProgressRef.current || 
      loadingMoreRef.current ||
      !hasStockVideoAccess
    ) {
      console.log('[StockVideos] Skipping loadMore - conditions not met');
      return;
    }
    
    // Mark as loading more locally
    loadingMoreRef.current = true;
    
    const nextPage = page + 1;
    console.log(`[StockVideos] 🔄 Loading more stock videos (page ${nextPage})`);
    
    try {
      await loadStockVideos(nextPage);
    } catch (err) {
      console.error('[StockVideos] ❌ Error loading more stock videos:', err);
      setError('Failed to load more stock videos. ' + (err.message || ''));
    } finally {
      loadingMoreRef.current = false;
    }
  }, [page, loading, hasMore, allStockVideosLoaded, hasStockVideoAccess, loadStockVideos]);

  // Function to load saved stock video IDs
  const loadSavedStockVideoIds = async () => {
    if (!user || !currentNamespaceId || loadingSavedStockVideos || !hasStockVideoAccess) return;

    try {
      setLoadingSavedStockVideos(true);
      console.log(`[StockVideos] Fetching saved stock videos for namespace: ${currentNamespace} (${currentNamespaceId})`);
      const savedIds = await getSavedStockVideoIds(currentNamespaceId);
      console.log(`[StockVideos] Found ${savedIds.length} saved stock videos in namespace:`, savedIds);
      setSavedStockVideoIds(new Set(savedIds || []));
      setSavedStockVideosLoaded(true);
    } catch (error) {
      console.error('Error loading saved stock video IDs:', error);
      setSavedStockVideosLoaded(true);
      setSavedStockVideoIds(new Set());
    } finally {
      setLoadingSavedStockVideos(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (user && currentNamespaceId && hasStockVideoAccess) {
      loadSavedStockVideoIds();
    } else {
      // Clear saved stock videos when logged out, no namespace, or no access
      setSavedStockVideoIds(new Set());
      setSavedStockVideosLoaded(true);
    }
  }, [user, currentNamespace, currentNamespaceId, hasStockVideoAccess]);

  // Load stock videos when filters change
  useEffect(() => {
    // Load videos for all users, but apply restrictions for non-premium users
    setInitialLoading(true);
    setIsDataReady(false);
    setAllStockVideosLoaded(false);
    loadStockVideos(1, true);
  }, [activeFilter, searchQuery, sortMethod, showSavedStockVideos]); // Removed hasStockVideoAccess dependency

  // Infinite scroll observer
  useEffect(() => {
    // Only set up observer if user has full access (Premium or Enterprise Premium)
    if (!hasFullStockVideoAccess) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !loading && !allStockVideosLoaded && !apiRequestInProgressRef.current) {
          console.log('[StockVideos] 👁️ Sentinel element is visible, loading more stock videos');
          loadMoreStockVideos();
        }
      },
      { 
        root: null, // Use viewport
        rootMargin: '600px 0px', // Load more when within 600px of viewport bottom
        threshold: 0.1 // 10% of the target is visible
      }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
      console.log('[StockVideos] 👁️ Observer attached to sentinel element');
    }

    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
        console.log('[StockVideos] 👁️ Observer detached from sentinel element');
      }
    };
  }, [loadMoreStockVideos, hasMore, loading, allStockVideosLoaded, hasFullStockVideoAccess]);

  // Handle stock video click
  const handleStockVideoClick = (stockVideo) => {
    setSelectedStockVideo(stockVideo);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedStockVideo(null);
  };

  const navigateStockVideo = (direction) => {
    if (!selectedStockVideo) return;
    
    const currentIndex = stockVideos.findIndex(video => video.id === selectedStockVideo.id);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = currentIndex + 1;
      if (newIndex >= stockVideos.length) {
        newIndex = 0; // Loop to first
      }
    } else {
      newIndex = currentIndex - 1;
      if (newIndex < 0) {
        newIndex = stockVideos.length - 1; // Loop to last
      }
    }
    
    setSelectedStockVideo(stockVideos[newIndex]);
  };

  // Handle save/unsave stock video
  const handleSaveStockVideo = async (stockVideoId, shouldSave) => {
    if (!user) {
      return; // Silent failure like templates
    }

    // Check if user has access to stock videos
    if (!hasStockVideoAccess) {
      if (isEnterpriseUser) {
        // Enterprise users without access go to app home, not pricing
        navigate('/app/', { replace: true });
      } else {
        // Non-enterprise users without access go to account page
        navigate('/app/account', { 
          state: { 
            fromFeature: true,
            returnPath: '/app/stockVideos',
            featureName: 'Stock Videos'
          } 
        });
      }
      return;
    }

    if (!currentNamespaceId) {
      return; // Silent failure like templates
    }

    // DEBUG: Log the save operation details
    console.log('[DEBUG] Stock video save operation:', {
      stockVideoId,
      shouldSave,
      userId: user?.uid,
      userEmail: user?.email,
      currentNamespace,
      currentNamespaceId,
      currentNamespaceObj,
      namespaceOwner: currentNamespaceObj?.userId,
      userOwnsNamespace: currentNamespaceObj?.userId === user?.uid,
      userPermission: currentNamespaceObj?.currentUserPermission,
      allNamespaces: namespaces.map(ns => ({ 
        name: ns.name, 
        id: ns.id, 
        userId: ns.userId,
        userOwnsIt: ns.userId === user?.uid,
        userPermission: ns.currentUserPermission 
      }))
    });

    // Optimistically update UI first
    if (shouldSave) {
      setSavedStockVideoIds(prev => new Set([...prev, stockVideoId]));
    } else {
      setSavedStockVideoIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(stockVideoId);
        return newSet;
      });
    }

    try {
      if (shouldSave) {
        await saveStockVideo(stockVideoId, currentNamespaceId);
      } else {
        await unsaveStockVideo(stockVideoId, currentNamespaceId);
      }
      
      // If we're viewing saved stock videos, refresh the list
      if (showSavedStockVideos) {
        loadStockVideos(1, true);
      }
    } catch (error) {
      console.error('Error saving/unsaving stock video:', error);
      
      // Handle specific premium access error
      if (error.response?.status === 403) {
        // Only redirect to account page if user actually doesn't have premium access
        if (!hasStockVideoAccess && !isEnterpriseUser) {
          navigate('/app/account', { 
            state: { 
              fromFeature: true,
              returnPath: '/app/stockVideos',
              featureName: 'Stock Videos'
            } 
          });
        }
        // For premium users, just log the error silently - no toast notifications
      }
      
      // Revert the UI change on error
      if (shouldSave) {
        setSavedStockVideoIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(stockVideoId);
          return newSet;
        });
      } else {
        setSavedStockVideoIds(prev => new Set([...prev, stockVideoId]));
      }
    }
  };

  // Search handlers
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleSortChange = (newSortMethod) => {
    setSortMethod(newSortMethod);
  };

  const handleToggleSavedFilter = () => {
    // If trying to see saved stock videos but no namespace is selected
    if (!showSavedStockVideos && !currentNamespaceId) {
      return; // Silent failure like templates
    }
    
    const newValue = !showSavedStockVideos;
    setShowSavedStockVideos(newValue);
    
    // When turning off the saved filter, refresh saved stock video IDs from the backend
    if (!newValue) {
      if (user && currentNamespaceId) {
        getSavedStockVideoIds(currentNamespaceId).then(savedIds => {
          console.log(`[StockVideos] Refreshing saved stock video IDs when disabling saved filter:`, savedIds);
          setSavedStockVideoIds(new Set(savedIds || []));
        }).catch(err => {
          console.error('[StockVideos] Error refreshing saved stock video IDs:', err);
          setSavedStockVideoIds(new Set());
        });
      } else {
        setSavedStockVideoIds(new Set());
      }
    }
  };

  // Calculate the number of columns for the skeleton based on screen width
  const getSkeletonColumns = () => {
    const width = window.innerWidth;
    if (width < 1024) return 2; // Below lg: 2 columns
    return 3; // lg and up: 3 columns
  };

  return (
    <div className="p-4 sm:p-6 pb-20 w-full max-w-[1600px] mx-auto">
      <SEOHead 
        pageType="app-stock-videos"
      />
      {/* Premium feature notification - show different messages for different tiers */}
      {!hasStockVideoAccess && (
        <div className="mb-6 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800/50 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Crown className="h-5 w-5 text-purple-500 dark:text-purple-400 mr-3" />
              <p className="text-sm text-purple-800 dark:text-purple-300">
                {isEnterpriseUser 
                  ? "Stock Videos is not enabled for your enterprise account. Contact your administrator for access."
                  : "Stock Videos is a premium feature. Upgrade to access our collection of professional stock videos."
                }
              </p>
            </div>
            {!isEnterpriseUser && (
              <button 
                onClick={() => navigate('/app/account', { state: { fromFeature: true, returnPath: '/app/stockVideos', featureName: 'Stock Videos' }})}
                className="ml-4 flex items-center px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-md text-xs font-medium"
              >
                Upgrade
                <ChevronRight className="ml-1 h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Stock Videos
            </h1>
          </div>
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <p>Browse and select from our collection of 100+ professional stock videos</p>
            <Tooltip 
              text={
                <div className="space-y-2">
                  <p className="font-medium">AI-Generated Stock Videos</p>
                  <p>Our stock video collection features high-quality videos created with cutting-edge AI technology, including Google's VEO3 model. These videos are completely free for you to use for any purpose - commercial, personal, or otherwise.</p>
                  <p>Each video is professionally crafted to enhance your advocacy campaigns, presentations, and creative projects. Download and use them without any licensing restrictions or attribution requirements.</p>
                  <p>We currently have 100+ stock videos available, with new additions regularly added to keep your content fresh and engaging.</p>
                </div>
              }
            >
              <HelpCircle className="ml-1.5 w-4 h-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
            </Tooltip>
          </div>
        </div>
      </div>
      
      {/* Search Component - on its own line */}
      <div className="mb-4">
        <StockVideoSearch 
          searchQuery={searchQuery} 
          setSearchQuery={setSearchQuery} 
          onSearch={handleSearch}
          onClearSearch={handleClearSearch}
          isLoading={searchLoading}
          isBasicUser={!hasFullStockVideoAccess}
        />
      </div>
      
      {/* Filter Component - on its own line */}
      <div className="mb-6">
        <StockVideoFilters 
          activeFilter={activeFilter} 
          setActiveFilter={setActiveFilter} 
          sortMethod={sortMethod}
          setSortMethod={handleSortChange}
          showSavedStockVideos={showSavedStockVideos}
          setShowSavedStockVideos={handleToggleSavedFilter}
          stockVideoCount={stockVideos.length}
          searchLoading={searchLoading}
          isPremiumUser={hasFullStockVideoAccess}
        />
      </div>
      
      {/* Error message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}
      
      {/* Clean, centered loading indicator for initial loading */}
      {(initialLoading || loadingSavedStockVideos) && !showSavedStockVideos && (
        <div className="space-y-6">
          {/* Show skeleton grid immediately for better perceived performance */}
          <div className="masonry-wrapper">
            <div className="masonry-grid">
              {Array.from({ length: getSkeletonColumns() }, (_, columnIndex) => (
                <div key={`skeleton-column-${columnIndex}`} className="masonry-column" style={{ width: `${100 / getSkeletonColumns()}%` }}>
                  {[0, 1, 2, 3, 4].map(itemIndex => (
                    <div key={`skeleton-${columnIndex}-${itemIndex}`} className="masonry-item p-1">
                      <div className="masonry-item-inner bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse">
                        <div className="w-full aspect-video bg-gradient-to-b from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 rounded-lg"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Grid Component - show for all users */}
      {(!initialLoading && !loadingSavedStockVideos || showSavedStockVideos) && (
        <>
          {stockVideos.length > 0 ? (
            <div className="animate-fade-in" style={{ animation: 'fadeIn 0.3s ease-out' }}>
              <StockVideoGrid
                stockVideos={stockVideos}
                onStockVideoClick={handleStockVideoClick}
                onSaveStockVideo={handleSaveStockVideo}
                savedStockVideoIds={savedStockVideoIds}
                loading={loading && page > 1}
                skeletonItems={[]}
                isPremiumUser={hasFullStockVideoAccess}
              />
              
              {/* Sentinel element for infinite scroll - only show if user can load more */}
              {hasFullStockVideoAccess && hasMore && !allStockVideosLoaded && (
                <div 
                  ref={sentinelRef}
                  className="h-10 my-6 flex justify-center" 
                >
                  {loading && (
                    <div className="flex justify-center items-center py-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-100 dark:border-gray-800 border-b-purple-600 dark:border-b-purple-400"></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            // Only show "No Stock Videos Found" if we have confirmed data state and no videos
            isDataReady && (
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                  <AlertCircle className="w-8 h-8 text-gray-600 dark:text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  No Stock Videos Found
                </h3>
                <p className="text-gray-600 dark:text-gray-400 max-w-md">
                  {showSavedStockVideos 
                    ? 'You have not saved any stock videos yet. Browse stock videos and click the heart icon to save them.'
                    : searchQuery || activeFilter 
                      ? 'Try adjusting your search or filter criteria to find stock videos.'
                      : 'Our stock video collection is being updated. Please check back soon for professional stock videos to enhance your campaigns.'}
                </p>
                {(searchQuery || activeFilter) && !showSavedStockVideos && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setActiveFilter(null);
                    }}
                    className="mt-4 px-4 py-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600 text-white rounded-lg transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )
          )}
        </>
      )}

      {/* Stock Video Modal */}
      {isModalOpen && selectedStockVideo && hasStockVideoAccess && (
        <StockVideoModal
          stockVideo={selectedStockVideo}
          isOpen={isModalOpen}
          onClose={closeModal}
          onNext={() => navigateStockVideo('next')}
          onPrevious={() => navigateStockVideo('previous')}
          hasNavigation={stockVideos.length > 1}
          isSaved={savedStockVideoIds.has(selectedStockVideo.id)}
          onSaveStockVideo={handleSaveStockVideo}
          isLoggedIn={!!user}
          userPlan={user?.planLevel || 'basic'}
        />
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default StockVideos; 